/**
 * Monthly Appointments — total scheduled appointments per month across
 * the current scope (group or a single clinic). Renders as a bar chart
 * with a horizontal reference line at the 720 target (per Davinder's
 * capacity math: 720 shows/mo/clinic = 100% utilization).
 *
 * Data source: same `report.summary.scheduled` series that powers the
 * show-rate line chart — no new endpoint required.
 */
import { Card } from './ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell } from 'recharts';

const TARGET_LINE = 720;
const OK_COLOR = '#839E97';
const WARN_COLOR = '#B08A3A';
const BAD_COLOR = '#8A3A3A';

function barColor(v, target) {
  if (v == null) return '#E8E5EC';
  const pct = v / target;
  if (pct >= 0.9) return OK_COLOR;
  if (pct >= 0.7) return WARN_COLOR;
  return BAD_COLOR;
}

export default function MonthlyAppointmentsBar({ report, scope, months, testId = 'chart-monthly-appointments' }) {
  if (!report?.summary?.scheduled?.length) return null;
  const clinicsInScope = scope === 'group' ? (report.clinics?.length || 1) : 1;
  const target = TARGET_LINE * clinicsInScope;
  const data = (report.months || []).map((m, i) => ({
    label: m.label,
    scheduled: report.summary.scheduled[i] || 0,
  }));
  return (
    <Card className="p-4 rounded-2xl border-[#E8E5EC]" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div>
          <div className="font-serif text-lg text-[#2D2833]">Monthly appointments</div>
          <div className="text-xs text-[#685B72]">
            {scope === 'group' ? 'All clinics' : scope} · last {months} months
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#685B72]">
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5" style={{ backgroundColor: '#2D2833' }} />
            Target {target.toLocaleString()}/mo
          </span>
        </div>
      </div>
      <p className="text-xs text-[#685B72] mb-3" data-testid={`${testId}-desc`}>
        This bar chart shows the total number of appointments scheduled each month across {scope === 'group' ? 'all clinics' : scope}. The dotted line marks the {target.toLocaleString()}/month target ({clinicsInScope > 1 ? `${clinicsInScope} clinics × ` : ''}720 shows per clinic = 100% utilization).
      </p>
      <div style={{ width: '100%', height: 220, minHeight: 220 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1EDF5" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#685B72' }} />
            <YAxis tick={{ fontSize: 11, fill: '#685B72' }} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E8E5EC' }}
              formatter={(v) => [Number(v).toLocaleString(), 'Scheduled']}
            />
            <ReferenceLine
              y={target}
              stroke="#2D2833"
              strokeDasharray="4 4"
              label={{ value: `${target.toLocaleString()} target`, position: 'insideTopRight', fontSize: 10, fill: '#2D2833' }}
            />
            <Bar dataKey="scheduled" radius={[6, 6, 0, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={barColor(d.scheduled, target)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
